import { useState, useEffect, useCallback, useRef } from "react";
import useDebounce from "../use-debounce/index";
import useToast from "../use-toast/index";

const useInfiniteScroll = (api, serializer, entity, params) => {
    const { initialPage = 1, itemsPerPage = 10 } = params;
    const toastType = { success: "success", error: "error" };
    const [data, setData] = useState([]);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [search, setSearch] = useState('');
    const [totalCount, setTotalCount] = useState(0);
    const { toastList, addToastMessage } = useToast();

    const prevOrgIdRef = useRef(params.orgId);
    const isLoadingRef = useRef(isLoading);
    const hasMoreDataRef = useRef(hasMoreData);
    const currentPageRef = useRef(currentPage);
    const searchRef = useRef(search);

    useEffect(() => {
        isLoadingRef.current = isLoading;
        hasMoreDataRef.current = hasMoreData;
        currentPageRef.current = currentPage;
        searchRef.current = search;
    }, [isLoading, hasMoreData, currentPage, search]);

    useEffect(() => {
        if (!isLoading && api) {
            const parameters = {
                ...(search && { searchBy: search }),
                ...params,
                page: prevOrgIdRef.current === params.orgId ? currentPage : initialPage,
                count: itemsPerPage,
                pageSize: itemsPerPage,
                direction: "ASC"
            };

            if (search) {
                debouncedSearch();
            } else {
                fetchData(parameters, prevOrgIdRef.current, params.orgId);
            }
        };
    }, [currentPage, search, api, params.orgId]);

    useEffect(() => {
        if (params.orgId !== prevOrgIdRef.current) {
            handleSearchCancel();
            prevOrgIdRef.current = params.orgId;
        }
    }, [params.orgId, api]);

    const debouncedSearch = useDebounce(() => {
        setData([]);
        setIsLoading(true);
        fetchData({
            ...(search && { searchBy: search }),
            ...params,
            page: prevOrgIdRef.current === params.orgId ? currentPage : initialPage,
            count: itemsPerPage,
            pageSize: itemsPerPage,
            direction: "ASC"
        }, prevOrgIdRef.current, params.orgId);
    }, 'default');

    const fetchData = useCallback(async (parameters, prevOrgId, currentOrgId) => {
        if (!api) return;

        setError(null);
        setIsLoading(true);
        try {
            const params = parameters ?? "";
            const [errorResponse, dataResponse] = await api.getEnrolledPrincipals({ params });

            if (errorResponse) {
                setHasMoreData(false);
                throw new Error("Failed to fetch data");
            }

            const { meta, items } = dataResponse;

            if (!Array.isArray(items)) {
                setHasMoreData(false);
                throw new Error('Invalid data format from resource');
            }

            const newData = serializer
                ? items.map(entry => new entity(serializer(entry)))
                : items.map(data => new entity(data));

            const hasMoreData = meta && meta.currentPage < meta.pageCount;
            const currentPage = meta?.currentPage ?? initialPage;

            setData(prevData => aggregateData(prevData, newData, { currentPage, initialPage, prevOrgId, currentOrgId }));

            setHasMoreData(hasMoreData);
            setCurrentPage(currentPage);
            setTotalCount(Number(meta?.count) || 0);
        } catch (error) {
            setError(error);
            addToastMessage(error.message ?? "Failed to fetch data", toastType.error)
        } finally {
            setIsLoading(false);
        }
    }, [api, serializer, entity, initialPage]);

    const aggregateData = (prevData, newData, { currentPage, initialPage, prevOrgId, currentOrgId }) => {
        if (currentPage === initialPage || prevOrgId !== currentOrgId) {
            return newData;
        } else {
            return [...prevData, ...newData];
        }
    };

    const loadMore = () => {
        if (!isLoadingRef.current && hasMoreDataRef.current && api) {
            const parameters = {
                ...params,
                ...(searchRef.current && { searchBy: searchRef.current }),
                page: currentPageRef.current + 1,
                count: itemsPerPage,
                pageSize: itemsPerPage,
                direction: "ASC"
            };
            fetchData(parameters, prevOrgIdRef.current, params.orgId);
        }
    };

    const handleSearch = (event) => {
        const searchTerm = event.target.value;

        if (searchTerm !== searchRef.current) {
            setSearch(searchTerm);
            setCurrentPage(initialPage);
        }
    };

    const handleSearchCancel = () => {
        setSearch('');
        setData([]);
        setCurrentPage(initialPage);
    };

    return {
        data,
        hasMoreData,
        loadMore,
        isLoading,
        error,
        handleSearchCancel,
        handleSearch,
        search,
        toastList,
        totalCount,
    };
};

export default useInfiniteScroll;
